// src/components/group/NextMeetingSection.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './NextMeetingSection.css';

function NextMeetingSection({ group, groupId, token, apiUrl, isOpen, onToggle }) {
  const { t, i18n } = useTranslation();
  const [participantsStatus, setParticipantsStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchParticipantsStatus();
  }, [groupId, group.status, group.participants?.length, group.schedule?.status]);

  const fetchParticipantsStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/participants-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setParticipantsStatus(data);
      }
    } catch (error) {
      console.error('Error fetching participants status:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('groupPage.nextMeeting.noMeetingScheduled');
    
    const date = new Date(dateString);
    const locale = i18n.language === 'en' ? 'en-US' : 'sv-SE';
    
    const dateStr = date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const timeStr = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${dateStr} ${timeStr}`;
  };

  const formatAnswerTime = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'en' ? 'en-US' : 'sv-SE';
    
    const dateStr = date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric'
    });
    
    const timeStr = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${dateStr} ${timeStr}`;
  };

  if (!group.meetingFrequency?.dayOfWeek && !group.singleMeetingDate) {
    return null;
  }

  // ✅ SAFETY: Get participants arrays with fallbacks
  const comingParticipants = participantsStatus?.participants?.coming || [];
  const notComingParticipants = participantsStatus?.participants?.notComing || [];
  const noResponseParticipants = participantsStatus?.participants?.noResponse || [];

  return (
    <section className="settings-section">
      <div className="section-header" onClick={onToggle}>
        <h2>
          {t('groupPage.nextMeeting.title')}
          {participantsStatus && (
            <span className="meeting-date-inline">
              {formatDateTime(participantsStatus.nextMeeting)}
            </span>
          )}
        </h2>
        <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
      </div>
      
      {isOpen && (
        <div className="next-meeting-content">
          {loading ? (
            <div className="loading-state">
              <span>{t('groupPage.loading')}</span>
            </div>
          ) : participantsStatus ? (
            <>
              {/* ✅ De som kommer */}
              <div className="coming-section">
                <h3 className="coming-title" style={{ color: '#2e7d32' }}>
                  ✅ {t('groupPage.nextMeeting.coming')} ({comingParticipants.length})
                </h3>
                {comingParticipants.length > 0 ? (
                  <div className="participants-list">
                    {comingParticipants.map(p => (
                      <div key={p.id} className="participant-item">
                        <div className="participant-info">
                          <span className="participant-name">
                            {p.isKeyPerson && <span className="key-person-badge">⭐ </span>}
                            {p.name?.trim().replace(/,+$/, '')}
                          </span>
                          <span className="participant-email">{p.email}</span>
                        </div>
                        <span className="participant-time">
                          {formatAnswerTime(p.answeredAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-message">
                    {t('groupPage.nextMeeting.noComing')}
                  </div>
                )}
              </div>

              {/* ❌ De som inte kommer */}
              <div className="coming-section" style={{ marginTop: '24px' }}>
                <h3 className="coming-title" style={{ color: '#c62828' }}>
                  ❌ {t('groupPage.nextMeeting.notComing')} ({notComingParticipants.length})
                </h3>
                {notComingParticipants.length > 0 ? (
                  <div className="participants-list">
                    {notComingParticipants.map(p => (
                      <div key={p.id} className="participant-item" style={{ opacity: 0.7 }}>
                        <div className="participant-info">
                          <span className="participant-name">
                            {p.isKeyPerson && <span className="key-person-badge">⭐ </span>}
                            {p.name?.trim().replace(/,+$/, '')}
                          </span>
                          <span className="participant-email">{p.email}</span>
                        </div>
                        <span className="participant-time">
                          {formatAnswerTime(p.answeredAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-message">
                    {t('groupPage.nextMeeting.noNotComing')}
                  </div>
                )}
              </div>

              {/* ⏳ De som inte svarat */}
              <div className="coming-section" style={{ marginTop: '24px' }}>
                <h3 className="coming-title" style={{ color: '#f57c00' }}>
                  ⏳ {t('groupPage.nextMeeting.noResponse')} ({noResponseParticipants.length})
                </h3>
                {noResponseParticipants.length > 0 ? (
                  <div className="participants-list">
                    {noResponseParticipants.map(p => (
                      <div key={p.id} className="participant-item" style={{ opacity: 0.5 }}>
                        <div className="participant-info">
                          <span className="participant-name">
                            {p.isKeyPerson && <span className="key-person-badge">⭐ </span>}
                            {p.name?.trim().replace(/,+$/, '')}
                          </span>
                          <span className="participant-email">{p.email}</span>
                        </div>
                        <span className="participant-time" style={{ color: '#f57c00' }}>
                          {t('groupPage.nextMeeting.noAnswer')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-message">
                    {/* Only show "all responded" if there are actually participants */}
                    {(comingParticipants.length > 0 || notComingParticipants.length > 0) 
                      ? t('groupPage.nextMeeting.allResponded')
                      : t('groupPage.nextMeeting.noParticipants')}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="error-state">
              {t('groupPage.errors.couldNotLoadStatus')}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default NextMeetingSection;
